import {
  ChakraBox,
  ChakraFlex,
  CheckboxV2,
  MultiSelectOption,
  SkeletonLoader,
  Text,
  TextInputV2,
  UniversalSelect,
} from '@equip.health/ui';
import { Dispatch, SetStateAction } from 'react';
import { CarePlanEvents } from '~/analytics/carePlanEvents';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { RefCarePlanManifestationResponse } from '~/lib/types/carePlan/RefCarePlanManifestationResponse';
import {
  getNotYetAssessedCode,
  useCarePlanFrequencyOptions,
} from '~/lib/hooks/useCarePlanFrequencyOptions';
import { ManifestationCodeToEditableDataMap } from '~/lib/types/carePlan/ManifestationCodeToEditableDataMap';
import { RESOLVED_FREQUENCY_CODES } from '~/lib/constants/carePlan.constants';

type Props = {
  manifestation: RefCarePlanManifestationResponse;
  selectedManifestationsMap: ManifestationCodeToEditableDataMap;
  setSelectedManifestationsMap: Dispatch<
    SetStateAction<ManifestationCodeToEditableDataMap>
  >;
  isForEditProblem?: boolean;
  modalId?: string;
};
export const AddEditManifestationRow = ({
  manifestation,
  selectedManifestationsMap,
  setSelectedManifestationsMap,
  isForEditProblem,
  modalId,
}: Props) => {
  const { trackEvent } = useAnalytics();
  const {
    carePlanProviderFacingLanguage: isProviderFacingLanguageEnabled,
    manifestationProblemStatusFrequency: isFrequencyDisplayEnabled,
  } = useFlags<LDFlags>();
  const {
    options: frequencyOptions,
    placeholder: frequencyPlaceholder,
    isLoading: isGetFrequenciesLoading,
  } = useCarePlanFrequencyOptions({
    frequencyUnitType: manifestation.frequencyUnitType,
    frequencyCode:
      selectedManifestationsMap[manifestation.code]?.frequencyCode,
  });
  const isSelected = manifestation.code in selectedManifestationsMap;

  return (
    <SkeletonLoader
      id={`add-edit-manifestation-row-loader-${manifestation.code}`}
      isLoaded={!isGetFrequenciesLoading}
    >
      <ChakraFlex flexDirection="column" gap="12px">
        <CheckboxV2
          id={`problem-manifestation-${manifestation.code}`}
          isChecked={isSelected}
          isLabelBolded={isSelected}
          label={manifestation.label}
          onChange={(isChecked) => {
            trackEvent(
              isForEditProblem
                ? CarePlanEvents.clickOnManifestationCheckboxInEditProblemModal
                : CarePlanEvents.clickOnManifestationCheckboxInAddProblemModal,
            );
            setSelectedManifestationsMap((prev) => {
              if (!isChecked) {
                const newManifestations = { ...prev };
                delete newManifestations[manifestation.code];
                return newManifestations;
              }
              return {
                ...prev,
                [manifestation.code]: {
                  description: '',
                  statusCode: 'ACTIVE',
                  ...(manifestation.frequencyUnitType !== null
                    ? {
                        frequencyCode: getNotYetAssessedCode(
                          manifestation.frequencyUnitType,
                        ),
                      }
                    : {}),
                },
              };
            });
          }}
        />
        {isSelected && (
          <ChakraBox paddingLeft="30px">
            <TextInputV2
              id={`problem-manifestation-description-${manifestation.code}`}
              onChange={(newDescription) => {
                setSelectedManifestationsMap((prev) => ({
                  ...prev,
                  [manifestation.code]: {
                    ...prev[manifestation.code],
                    description: newDescription,
                  },
                }));
              }}
              onFocus={() =>
                trackEvent(
                  isForEditProblem
                    ? CarePlanEvents.clickIntoManifestationDescriptionTextboxInEditProblemModal
                    : CarePlanEvents.clickIntoManifestationDescriptionTextboxInAddProblemModal,
                )
              }
              placeholder="Please add any additional context"
              value={selectedManifestationsMap[manifestation.code].description}
            />
            {(isProviderFacingLanguageEnabled || isFrequencyDisplayEnabled) && (
              <ChakraFlex alignItems="center" gap="12px" marginTop="12px">
                {isProviderFacingLanguageEnabled &&
                  manifestation.providerFacingLanguage && (
                    <Text>{manifestation.providerFacingLanguage}</Text>
                  )}
                {isFrequencyDisplayEnabled &&
                  manifestation.frequencyUnitType && (
                    <ChakraBox width="200px">
                      <UniversalSelect
                        ariaLabel="Select frequency"
                        id={`add-problem-select-manifestation-frequency-${manifestation.code}`}
                        isClearable={false}
                        isSingleSelect
                        menuPlacement="auto"
                        modalId={modalId}
                        onChange={(newFrequency: MultiSelectOption) => {
                          setSelectedManifestationsMap((prev) => ({
                            ...prev,
                            [manifestation.code]: {
                              ...prev[manifestation.code],
                              frequencyCode: newFrequency.value,
                              statusCode: RESOLVED_FREQUENCY_CODES.includes(
                                newFrequency.value,
                              )
                                ? 'RESOLVED'
                                : 'ACTIVE',
                            },
                          }));
                        }}
                        options={frequencyOptions}
                        placeholder={frequencyPlaceholder}
                        value={frequencyOptions.find(
                          (option) =>
                            option.value ===
                            selectedManifestationsMap[manifestation.code]
                              .frequencyCode,
                        )}
                        variant="small"
                      />
                    </ChakraBox>
                  )}
                {isFrequencyDisplayEnabled &&
                  !manifestation.frequencyUnitType &&
                  isForEditProblem && (
                    <CheckboxV2
                      id={`add-edit-manifestation-status-${manifestation.code}`}
                      isChecked={
                        selectedManifestationsMap[manifestation.code]
                          .statusCode === 'RESOLVED'
                      }
                      label="Mark resolved"
                      onChange={(isChecked) => {
                        setSelectedManifestationsMap((prev) => ({
                          ...prev,
                          [manifestation.code]: {
                            ...prev[manifestation.code],
                            statusCode: isChecked ? 'RESOLVED' : 'ACTIVE',
                          },
                        }));
                      }}
                    />
                  )}
              </ChakraFlex>
            )}
          </ChakraBox>
        )}
      </ChakraFlex>
    </SkeletonLoader>
  );
};
